import { DatePickerProps } from 'antd';
import { useBookings } from './useBookings';
import dayjs, { Dayjs } from 'dayjs';
import { Booking } from 'src/api/bookings';

export const useAvailableBookingTime = (
  colaboratorID: string | null,
  currentBooking?: Booking,
): DatePickerProps['disabledTime'] => {
  const { bookingsQuery } = useBookings();
  if (!colaboratorID) {
    return undefined;
  }

  const colaboratorBookings =
    bookingsQuery.data?.filter((c) => c.colaboratorID === colaboratorID) || [];

  if (colaboratorBookings.length === 0) {
    return undefined;
  }

  return (date) => {
    // Find all bookings on that date
    const currentDayBookings = colaboratorBookings.filter(
      (b) => b.date.substring(0, 10) === date.toISOString().substring(0, 10),
    );

    // No bookings on that day then allow every hour
    if (currentDayBookings.length === 0) {
      return {};
    }

    const disabledHours: number[] = [];
    const sorted = currentDayBookings
      .filter((b) => b.id !== currentBooking?.id)
      .sort((a, b) => a.date.localeCompare(b.date));

    sorted.forEach((booking) => {
      if (booking.serviceDurationInMinutes >= 120) {
        // Eg. If service starts at 12:10 and spans for 2 hours we know that 13h block will be occupied
        for (let i = 0; i < booking.serviceDurationInMinutes / 2; ++i)
          disabledHours.push(new Date(booking.date).getHours() + i + 1);
      }
    });
    return {
      disabledHours: () => disabledHours,
      disabledMinutes: (hour) => {
        // Get all the services that start at or before this hour and spans into this hour
        const servicesBeforeOrAtThisHour = sorted.filter((b) => {
          return (
            new Date(b.date).getHours() <= hour &&
            dayjs(b.date)
              .add(b.serviceDurationInMinutes, 'minutes')
              .isAfter(dayjs(date).set('hour', hour))
          );
        });

        const disabledMinutes: Record<number, null> = {};
        servicesBeforeOrAtThisHour.forEach((s) => {
          const startDate = dayjs(s.date);
          const endDate = dayjs(s.date).add(
            s.serviceDurationInMinutes,
            'minutes',
          );
          if (startDate.hour() < hour && endDate.hour() > hour) {
            Array(60)
              .fill(0)
              .forEach((_, i) => {
                disabledMinutes[i] = null;
              });
          }
          if (startDate.hour() < hour && endDate.hour() === hour) {
            Array(endDate.minute())
              .fill(0)
              .forEach((_, i) => {
                disabledMinutes[i] = null;
              });
          }

          if (startDate.hour() === hour && endDate.hour() > hour) {
            Array(60 - startDate.minute())
              .fill(0)
              .forEach((_, i) => {
                disabledMinutes[i + startDate.minute()] = null;
              });
          }
          if (startDate.hour() === hour && endDate.hour() === hour) {
            for (let i = startDate.minute(); i < endDate.minute(); ++i) {
              disabledMinutes[i] = null;
            }
          }
        });

        return Object.keys(disabledMinutes).map(Number);
      },
    };
  };
};
